import React, { useContext, useEffect, useRef, useState } from 'react'

import EditorAddExternalAttchments from '@/components/shared/components/attachments/EditorAddExternalAttchments'
import type { IEditorExternalAttchmentsListProps } from '@/components/shared/components/attachments/EditorExternalAttchmentsList'
import EditorExternalAttchmentsList from '@/components/shared/components/attachments/EditorExternalAttchmentsList'
import RichTextEditor from '@/components/shared/components/editor/RichTextEditor'
import Button from '@/components/shared/ui/Button'
import ButtonDropdown from '@/components/shared/ui/ButtonDropdown'
import { ButtonDropdownItem } from '@/components/shared/ui/ButtonDropdown/ButtonDropdown'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import {
  cleanUserForSegment,
  recordEvent,
  stateKeyValueChange,
  stateMerge,
} from '@/lib/helpers/appHelpers'
import {
  getCommentFromLocalDB,
  removeCommentFromLocalDB,
  setCommentToLocalDB,
} from '@/lib/helpers/modules/commentHelper'
import { isUserMember } from '@/lib/helpers/modules/userHelper'
import { isHTMLContentEmpty } from '@/lib/helpers/stringHelpers'
import {
  type IResources,
  addResourceComment,
  updateResourceComment,
} from '@/models/Resource'
import type { IComment, ICreateCommentData } from '@/types/comment'
import type { IEditorRef } from '@/types/editor'
import toaster from '@/utils/toast'

interface IPropTypes {
  resourceId: string | number
  resourceModule: IResources
  parentComment?: IComment
  onNewComment?: (comment: IComment) => void
  onUpdate?: (comment: IComment) => void
  comment?: IComment
  onToggleForm?: (status: boolean) => void
  onlyInternal?: boolean
  autoSaveKey?: string
  editorTextOnlyMenu?: boolean
  enableAttachments?: boolean
}
export default function ResourceCommentForm({
  resourceId,
  resourceModule,
  onNewComment,
  onUpdate,
  parentComment,
  comment,
  onToggleForm,
  onlyInternal = false,
  autoSaveKey,
  editorTextOnlyMenu = false,
  enableAttachments = false,
}: IPropTypes) {
  const t = useTranslations('comments.newComment')
  const { userProfile, organization } = useContext(HNContext)
  const [commentData, setCommentData] = useState<ICreateCommentData>({
    id: comment?.id,
    comment_html: comment?.comment || '',
    internal_comment: comment?.internal || false,
    parent_id: comment?.parent_id || parentComment?.id,
    attachments: undefined,
  })
  const [commenting, setCommenting] = useState(false)
  const [internal, setInternal] = useState(onlyInternal)
  const commentFormEl = useRef<IEditorRef>(null)
  const attachmentUploaderRef = useRef<IEditorExternalAttchmentsListProps>(null)

  const handleSubmit = (content?: string): any => {
    const commentContent = content || commentData.comment_html
    const external_files = enableAttachments
      ? attachmentUploaderRef.current?.getAttachments().map((a) => a.url)
      : []
    let commentInternal
    if (
      (!commentContent.length || isHTMLContentEmpty(commentContent)) &&
      !external_files?.length
    ) {
      return
    }

    if (commentFormEl.current && !commentFormEl.current?.isReadyToSubmit()) {
      toaster.info({
        message: t('messages.imagesUploading', { ignorePrefix: true }),
      })
      return
    }

    setInternal((_prev) => {
      commentInternal = _prev
      return commentInternal
    })

    if (
      enableAttachments &&
      attachmentUploaderRef.current?.getInProgressFiles().length
    ) {
      toaster.error({
        message: t('messages.uploadInProgress'),
      })
      return
    }

    setCommenting(true)
    let promise: Promise<IComment>
    const commentPayload: ICreateCommentData = {
      ...commentData,
      comment_html: commentContent || '',
      internal_comment: commentInternal || internal,
      mentioned_user_ids: commentFormEl.current?.getMentions('Customer'),
      attachments: undefined,
    }
    if (comment) {
      promise = updateResourceComment(comment.id, commentPayload as any)
    } else {
      promise = addResourceComment(resourceId, resourceModule, commentPayload)
    }

    if (!comment) {
      recordEvent('RESOURCE_COMMENT', {
        resource_id: resourceId,
        comment: commentContent,
        isReply: Boolean(parentComment),
        user: cleanUserForSegment(userProfile),
      })
    }

    promise
      .then((newComment) => {
        if (comment && comment.id) {
          if (onUpdate) onUpdate(newComment)
        } else if (onNewComment) onNewComment(newComment)
        if (commentFormEl.current) {
          commentFormEl.current.reset()
        }
        setCommentData((oldData) => {
          return {
            ...oldData,
            comment_html: '',
          }
        })
        if (autoSaveKey) {
          removeCommentFromLocalDB(autoSaveKey)
        }
        attachmentUploaderRef.current?.resetAttachments()
      })
      .catch((err) => {
        toaster.error({ message: err.message })
      })
      .finally(() => {
        setCommenting(false)
      })
  }

  useEffect(() => {
    if (autoSaveKey) {
      setCommentToLocalDB(autoSaveKey, { autoSaveKey, ...commentData })
    }
  }, [commentData])

  useEffect(() => {
    if (autoSaveKey) {
      getCommentFromLocalDB(autoSaveKey).then((data) => {
        if (data) {
          stateMerge(
            { ...data, attachments: data.attachments || [] },
            setCommentData
          )
          commentFormEl.current?.setValue(data.comment_html)
        } else {
          stateMerge({ ...commentData, attachments: [] }, setCommentData)
        }
      })
    }
  }, [])

  const renderButtonDropdownItems = (): any => {
    const commentType = parentComment ? 'reply' : 'comment'
    const isEdit = comment?.id
    const buttons = []

    if (isEdit) {
      return (
        <ButtonDropdownItem
          key='edit'
          itemTitle={t(`actions.edit`)}
          variant='primary'
          itemKey='edit'
          onSelect={() => {
            setInternal(false)
          }}
        />
      )
    }
    if (!onlyInternal || !(parentComment && parentComment.internal)) {
      buttons.push(
        <ButtonDropdownItem
          key='public'
          itemTitle={t(`actions.${commentType}Publicly`)}
          itemDescription={t(`actions.commentPubliclyDescription`)}
          variant='primary'
          itemKey='public'
          onSelect={() => {
            setInternal(false)
          }}
        />
      )
    }
    if (isUserMember(userProfile)) {
      buttons.push(
        <ButtonDropdownItem
          key='internal'
          itemTitle={t(`actions.${commentType}Internally`)}
          itemDescription={t(`actions.commentInternallyDescription`)}
          variant='warning'
          itemKey='internal'
          onSelect={() => {
            setInternal(true)
          }}
        />
      )
    }
    return buttons
  }

  const renderCommentActions = () => {
    const defaultKey = internal ? 'internal' : 'public'

    return (
      <ButtonDropdown
        onClick={() => handleSubmit()}
        selectedKey={comment?.id ? 'edit' : defaultKey}
        loading={commenting}
        hideSelected={commentData.comment_html.length === 0}
        buttonProps={{
          disabled:
            isHTMLContentEmpty(commentData.comment_html) &&
            commentData.attachments?.length === 0,
        }}
      >
        {renderButtonDropdownItems()}
      </ButtonDropdown>
    )
  }

  if (!userProfile) return null
  if (!organization) return null

  return (
    <div className='w-full space-y-3 rounded'>
      <RichTextEditor
        ref={commentFormEl}
        defaultValue={commentData.comment_html}
        variant={internal ? 'yellow' : ''}
        onChange={(content) =>
          stateKeyValueChange('comment_html', content, setCommentData)
        }
        resource={{
          type: 'Comment',
          id: comment?.id,
        }}
        onSubmit={handleSubmit}
        placeholder={t('placeholder')}
        showBubbleMenu={false}
        showFixedMenu={false}
        editorMenu={false}
        editorTextOnlyMenu={editorTextOnlyMenu}
      />

      {commentData.attachments && (
        <EditorExternalAttchmentsList
          defaultAttachments={commentData.attachments || []}
          ref={attachmentUploaderRef}
          resourceId={comment?.id}
          resourceType={'Comment'}
          onUpdate={(attachments) => {
            stateKeyValueChange('attachments', attachments, setCommentData)
          }}
        />
      )}
      <div className='flex items-center justify-end space-x-0.5 sm:space-x-2'>
        {enableAttachments && (
          <EditorAddExternalAttchments
            onAdd={(files) => {
              attachmentUploaderRef.current?.attachFiles(files)
            }}
            buttonSize={'sm'}
          />
        )}

        {(parentComment || comment?.id) && (
          <Button
            size='xs'
            variant='secondary'
            onClick={() => {
              if (onToggleForm) onToggleForm(false)
            }}
          >
            {t('buttons.cancel', { ignorePrefix: true })}
          </Button>
        )}
        {renderCommentActions()}
      </div>
    </div>
  )
}
